import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Plus, X, Check } from "lucide-react";
import { CONDITIONS, MOBILITY_OPTIONS, SENSORY_OPTIONS } from "@/lib/constants";

function Toggle({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      aria-pressed={active}
      onClick={onClick}
      className={`min-h-[52px] w-full text-left px-4 rounded-xl border-2 font-medium transition-colors flex items-center justify-between ${
        active
          ? "border-[#EF9F27] bg-[#EF9F27]/15 text-[#F8FAFC]"
          : "border-[#333333] bg-[#1A1A1A] text-[#F8FAFC]"
      }`}
    >
      <span>{children}</span>
      {active && <Check size={20} className="text-[#EF9F27]" />}
    </button>
  );
}

function AddRow({ label, items, setItems, placeholder, testidPrefix }) {
  const [val, setVal] = useState("");
  const add = () => {
    const v = val.trim();
    if (!v) return;
    setItems([...items, v]);
    setVal("");
  };
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input
          data-testid={`${testidPrefix}-input`}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder={placeholder}
          aria-label={label}
          className="min-h-[52px] rounded-xl text-base"
        />
        <Button
          type="button"
          data-testid={`${testidPrefix}-add`}
          onClick={add}
          aria-label={`Add ${label}`}
          className="min-h-[52px] min-w-[52px] rounded-xl bg-[#1A1A1A] hover:bg-[#1A1A1A]/90"
        >
          <Plus size={22} />
        </Button>
      </div>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li
            key={i}
            className="flex items-center justify-between bg-[#1A1A1A] border border-[#333333] rounded-xl px-4 py-3"
          >
            <span className="text-[#F8FAFC]">{it}</span>
            <button
              type="button"
              data-testid={`${testidPrefix}-remove-${i}`}
              aria-label={`Remove ${it}`}
              onClick={() => setItems(items.filter((_, j) => j !== i))}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center text-[#666666] hover:text-[#D32F2F]"
            >
              <X size={20} />
            </button>
          </li>
        ))}
        {items.length === 0 && (
          <li className="text-sm text-[#666666] px-1">None added yet.</li>
        )}
      </ul>
    </div>
  );
}

export function ProfileWizard({ initial, onComplete, submitLabel = "Finish" }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [conditions, setConditions] = useState(initial?.conditions || []);
  const [otherCond, setOtherCond] = useState("");
  const [medications, setMedications] = useState(initial?.medications || []);
  const [allergies, setAllergies] = useState(initial?.allergies || []);
  const [mobility, setMobility] = useState(initial?.mobility_needs || "none");
  const [sensory, setSensory] = useState(initial?.sensory_needs || []);
  const [contactName, setContactName] = useState(initial?.emergency_contact_name || "");
  const [contactPhone, setContactPhone] = useState(initial?.emergency_contact_phone || "");
  const [comms, setComms] = useState(initial?.communication_needs || "");
  const [search, setSearch] = useState("");

  const STEPS = ["Conditions", "Medications", "Allergies", "Mobility", "Sensory", "Contact"];
  const total = STEPS.length;

  const toggleCond = (c) =>
    setConditions((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));
  const toggleSensory = (s) =>
    setSensory((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));

  const addOther = () => {
    const v = otherCond.trim();
    if (v && !conditions.includes(v)) setConditions([...conditions, v]);
    setOtherCond("");
  };

  const submit = async () => {
    setSaving(true);
    try {
      await onComplete({
        conditions,
        medications,
        allergies,
        mobility_needs: mobility,
        sensory_needs: sensory,
        communication_needs: comms,
        emergency_contact_name: contactName,
        emergency_contact_phone: contactPhone,
      });
    } finally {
      setSaving(false);
    }
  };

  const next = () => (step < total - 1 ? setStep(step + 1) : submit());
  const back = () => step > 0 && setStep(step - 1);

  const filtered = CONDITIONS.filter((c) =>
    c.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex flex-col min-h-[70vh]">
      <div className="mb-6">
        <div className="flex justify-between text-sm text-[#A1A1AA] mb-2">
          <span className="font-medium">
            Step {step + 1} of {total}
          </span>
          <span>{STEPS[step]}</span>
        </div>
        <Progress
          value={((step + 1) / total) * 100}
          className="h-2 bg-[#333333]"
          data-testid="wizard-progress"
        />
      </div>

      <div className="flex-1">
        {step === 0 && (
          <div className="space-y-4">
            <h2 className="font-head text-2xl font-bold">Which conditions do you live with?</h2>
            <p className="text-[#A1A1AA]">Select any that apply. You can add your own too.</p>
            <Input
              data-testid="condition-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search conditions…"
              aria-label="Search conditions"
              className="min-h-[52px] rounded-xl text-base"
            />
            <div className="grid grid-cols-1 gap-2">
              {filtered.map((c) => (
                <Toggle
                  key={c}
                  testid={`condition-${c.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                  active={conditions.includes(c)}
                  onClick={() => toggleCond(c)}
                >
                  {c}
                </Toggle>
              ))}
            </div>
            <div className="flex gap-2 pt-1">
              <Input
                data-testid="condition-other-input"
                value={otherCond}
                onChange={(e) => setOtherCond(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addOther())}
                placeholder="Other condition…"
                aria-label="Add other condition"
                className="min-h-[52px] rounded-xl text-base"
              />
              <Button
                type="button"
                data-testid="condition-other-add"
                onClick={addOther}
                aria-label="Add other condition"
                className="min-h-[52px] min-w-[52px] rounded-xl bg-[#1A1A1A] hover:bg-[#1A1A1A]/90"
              >
                <Plus size={22} />
              </Button>
            </div>
            {conditions.filter((c) => !CONDITIONS.includes(c)).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {conditions
                  .filter((c) => !CONDITIONS.includes(c))
                  .map((c) => (
                    <span
                      key={c}
                      className="inline-flex items-center gap-1 bg-[#EF9F27]/15 text-[#F8FAFC] rounded-full px-3 py-1 text-sm font-medium"
                    >
                      {c}
                      <button
                        type="button"
                        aria-label={`Remove ${c}`}
                        onClick={() => toggleCond(c)}
                      >
                        <X size={16} />
                      </button>
                    </span>
                  ))}
              </div>
            )}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <h2 className="font-head text-2xl font-bold">Current medications</h2>
            <p className="text-[#A1A1AA]">
              List medicines you take. We use this only to add travel notes — we never suggest
              dosages.
            </p>
            <AddRow
              label="medication"
              items={medications}
              setItems={setMedications}
              placeholder="e.g. Insulin (Novorapid)"
              testidPrefix="medication"
            />
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <h2 className="font-head text-2xl font-bold">Allergies</h2>
            <p className="text-[#A1A1AA]">Add any allergies you'd want a paramedic to know.</p>
            <AddRow
              label="allergy"
              items={allergies}
              setItems={setAllergies}
              placeholder="e.g. Penicillin"
              testidPrefix="allergy"
            />
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <h2 className="font-head text-2xl font-bold">Mobility</h2>
            <p className="text-[#A1A1AA]">Choose the option that best describes your needs.</p>
            <div className="grid grid-cols-1 gap-2">
              {MOBILITY_OPTIONS.map((m) => (
                <Toggle
                  key={m.value}
                  testid={`mobility-${m.value}`}
                  active={mobility === m.value}
                  onClick={() => setMobility(m.value)}
                >
                  {m.label}
                </Toggle>
              ))}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <h2 className="font-head text-2xl font-bold">Sensory needs</h2>
            <p className="text-[#A1A1AA]">Select any that apply. This personalises venue guidance.</p>
            <div className="grid grid-cols-1 gap-2">
              {SENSORY_OPTIONS.map((s) => (
                <Toggle
                  key={s.value}
                  testid={`sensory-${s.value}`}
                  active={sensory.includes(s.value)}
                  onClick={() => toggleSensory(s.value)}
                >
                  {s.label}
                </Toggle>
              ))}
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-4">
            <h2 className="font-head text-2xl font-bold">Emergency contact</h2>
            <p className="text-[#A1A1AA]">Who should we help you reach in an emergency?</p>
            <div className="space-y-2">
              <Label htmlFor="ec-name">Contact name</Label>
              <Input
                id="ec-name"
                data-testid="contact-name"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="e.g. Alex Taylor"
                className="min-h-[52px] rounded-xl text-base"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ec-phone">Contact phone</Label>
              <Input
                id="ec-phone"
                data-testid="contact-phone"
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="e.g. +44 7700 900000"
                className="min-h-[52px] rounded-xl text-base"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ec-comms">Communication needs (optional)</Label>
              <Textarea
                id="ec-comms"
                data-testid="communication-needs"
                value={comms}
                onChange={(e) => setComms(e.target.value)}
                placeholder="e.g. I am non-verbal; please write things down."
                className="rounded-xl text-base min-h-[88px]"
              />
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-3 pt-8 sticky bottom-0 bg-[#111111] py-4">
        {step > 0 && (
          <Button
            type="button"
            data-testid="wizard-back"
            onClick={back}
            variant="outline"
            className="min-h-[56px] flex-1 rounded-xl border-2 border-[#1A1A1A] text-[#F8FAFC] text-lg font-semibold"
          >
            Back
          </Button>
        )}
        <Button
          type="button"
          data-testid="wizard-next"
          onClick={next}
          disabled={saving}
          className="min-h-[56px] flex-1 rounded-xl bg-[#EF9F27] hover:bg-[#EF9F27] text-white text-lg font-semibold"
        >
          {step < total - 1 ? "Next" : saving ? "Saving…" : submitLabel}
        </Button>
      </div>
    </div>
  );
}
